import React, { useEffect } from 'react';
import { Container, Alert } from '@mui/material';
import Grid from '@mui/material/Grid';
import { CustomerIdentityPanel } from '@/components/iem/identity/CustomerIdentityPanel';
import { MainContentTabs } from '@/components/iem/panels/MainContentTabs';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useMyPersonEntityId } from '@/hooks/useMyPersonEntityId';
import { useIemFrontDoorStore } from '@/stores/iemFrontDoorStore';
import { useEntities } from '@/hooks';
import { LoadingSkeleton } from '@/components/shared/LoadingSkeleton';

/**
 * My Hand Receipt — personal hand-receipt view for linked operational users.
 *
 * Reuses the existing FrontWindow page components (CustomerIdentityPanel +
 * MainContentTabs), scoped to the current user's own person entity. The
 * selection is auto-applied from myPersonEntityId and cannot be changed by
 * the user from this route. No duplicate component is introduced.
 *
 * Implements #344 (additive access for CIF/HR/Comptroller/Finance linked users).
 */
export const MyHandReceiptPage: React.FC = () => {
  usePageTitle('My Hand Receipt');

  const { myPersonEntityId, isLoading: personLoading } = useMyPersonEntityId();
  const { setCustomer, currentCustomer } = useIemFrontDoorStore();

  // Fetch the person entity when we have the id
  const { data: entities, isLoading: entitiesLoading } = useEntities({
    type: 'person',
    enabled: !!myPersonEntityId,
  });

  // Auto-select the current user's own person entity into the store.
  // This mirrors what CustomerIdentityPanel does for service members, but
  // driven by myPersonEntityId rather than the service-member role.
  useEffect(() => {
    if (!myPersonEntityId || !entities) return;
    // Already selected — don't re-select to avoid flickering
    if (currentCustomer?.id === myPersonEntityId) return;

    const myEntity = entities.find((e) => e.id === myPersonEntityId);
    if (myEntity) {
      // Map entity to the IEMCustomer shape expected by the store
      setCustomer({
        id: myEntity.id,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...(myEntity.entity_data as any),
        individual_id: (myEntity.entity_data as Record<string, unknown>)?.individual_id as
          | string
          | undefined,
      });
    }
  }, [myPersonEntityId, entities, currentCustomer, setCustomer]);

  if (personLoading || entitiesLoading) {
    return <LoadingSkeleton />;
  }

  // Fail-closed: if myPersonEntityId cannot be resolved, show nothing.
  if (!myPersonEntityId) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <Alert severity="warning">
          Your account is not linked to a person entity. My Hand Receipt is not available.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {/* Left Column: Customer Identity (locked to own person — isServiceMember
            behaviour in CustomerIdentityPanel handles auto-select) */}
        <Grid size={{ xs: 12, md: 2 }}>
          <CustomerIdentityPanel lockToCurrentUser />
        </Grid>

        {/* Right Column: Main Content Tabs (hand receipt, THR, etc.) */}
        <Grid size={{ xs: 12, md: 10 }}>
          <MainContentTabs />
        </Grid>
      </Grid>
    </Container>
  );
};
